import { Button, Chip, Dialog, DialogActions, DialogContent, Stack, Tab, Tabs, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiResponse, err } from '../api/axios';
import {
  createDataSourceLayer,
  createDataSourceType,
  createDataSourceVector,
  deleteDataSourceLayer,
  deleteDataSourceType,
  deleteDataSourceVector,
  getDataSourceLayers,
  getDataSourceTypes,
  getDataSourceVectors,
  updateDataSourceLayer,
  updateDataSourceType,
  updateDataSourceVector,
} from '../api/super-admin';
import {
  DataSource,
  DataSourceLayer,
  DataSourceType,
  DataSourceVectorAdmin,
} from '../core/data-sources';
import {
  dataSourceLayerShape,
  dataSourceTypeShape,
  dataSourceVectorShape,
} from '../data/form-shapes';
import {
  layerFromFormValues,
  layerToFormValues,
  vectorFromFormValues,
  vectorToFormValues,
} from '../helpers/data-source-form-values';
import { ConfirmDeleteModal } from './confirm-delete-modal';
import { ErrorMessage } from './error-message';
import { CreateForm, FormObject } from './form-components';
import { ModalTitle } from './modal-title';

type EditTarget = number | 'new' | null;

const nextOrder = (items: { order?: number | null }[]) =>
  items.reduce((max, i) => Math.max(max, i.order || 0), 0) + 10;

interface DataSourceChildrenModalProps {
  dataSource: DataSource;
  initialTab?: number;
  handleClose: () => void;
}

export const DataSourceChildrenModal = ({
  dataSource,
  initialTab = 0,
  handleClose,
}: DataSourceChildrenModalProps) => {
  const [tabIndex, setTabIndex] = useState(initialTab);
  const [apiError, setApiError] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const [layers, setLayers] = useState<DataSourceLayer[]>([]);
  const [vectors, setVectors] = useState<DataSourceVectorAdmin[]>([]);
  const [types, setTypes] = useState<DataSourceType[]>([]);

  const [layerTarget, setLayerTarget] = useState<EditTarget>(null);
  const [vectorTarget, setVectorTarget] = useState<EditTarget>(null);

  // Layers and vectors are hard deletes — there is no restore for these.
  const [layerToDelete, setLayerToDelete] = useState<DataSourceLayer | null>(null);
  const [vectorToDelete, setVectorToDelete] = useState<DataSourceVectorAdmin | null>(null);
  const [deleting, setDeleting] = useState(false);

  const label = [dataSource.provider, dataSource.model, dataSource.geography, dataSource.version]
    .filter(Boolean)
    .join(' / ');

  const fetchChildren = useCallback(async () => {
    setLoading(true);
    const [layersRes, vectorsRes, typesRes] = await Promise.all([
      getDataSourceLayers(dataSource.id),
      getDataSourceVectors(dataSource.id),
      getDataSourceTypes(),
    ]);

    const errors: string[] = [];
    if (layersRes.error === 200 && layersRes.response) {
      setLayers(layersRes.response?.results || []);
    } else {
      errors.push(layersRes.message || 'An error occurred while fetching layers.');
    }
    if (vectorsRes.error === 200 && vectorsRes.response) {
      setVectors(vectorsRes.response?.results || []);
    } else {
      errors.push(vectorsRes.message || 'An error occurred while fetching vectors.');
    }
    if (typesRes.error === 200 && typesRes.response) {
      setTypes(typesRes.response?.results || []);
    } else {
      errors.push(typesRes.message || 'An error occurred while fetching vector types.');
    }

    setApiError(errors);
    setLoading(false);
  }, [dataSource.id]);

  useEffect(() => {
    fetchChildren();
  }, [fetchChildren]);

  const typeNameById = useMemo(() => {
    const map: Record<number, string> = {};
    types.forEach((t) => {
      map[t.id] = t.name || t.type || `#${t.id}`;
    });
    return map;
  }, [types]);

  const handleDeleteLayer = useCallback(async () => {
    if (!layerToDelete) return;
    setDeleting(true);
    const { error, message } = await deleteDataSourceLayer(layerToDelete.id);
    setDeleting(false);
    if (error === 200) {
      setApiError([]);
      setLayerToDelete(null);
      fetchChildren();
    } else {
      setApiError([message || `Couldn't delete layer ${layerToDelete.id}.`]);
    }
  }, [layerToDelete, fetchChildren]);

  const handleDeleteVector = useCallback(async () => {
    if (!vectorToDelete) return;
    setDeleting(true);
    const { error, message } = await deleteDataSourceVector(vectorToDelete.id);
    setDeleting(false);
    if (error === 200) {
      setApiError([]);
      setVectorToDelete(null);
      fetchChildren();
    } else {
      setApiError([message || `Couldn't delete vector ${vectorToDelete.id}.`]);
    }
  }, [vectorToDelete, fetchChildren]);

  const layerColumns: any = [
    { field: 'order', headerName: 'Order', type: 'number', width: 80, editable: false },
    { field: 'title', headerName: 'Title', width: 200, editable: false },
    { field: 'short_name', headerName: 'Short name', width: 140, editable: false },
    {
      field: 'key',
      headerName: 'Key',
      width: 160,
      editable: false,
      renderCell: (params: any) => {
        const value = params?.row?.key;
        return value === 'model-grid' ? <Chip size="small" label="model-grid" /> : value;
      },
    },
    { field: 'units', headerName: 'Units', width: 90, editable: false },
    { field: 'min_value', headerName: 'Min', type: 'number', width: 90, editable: false },
    { field: 'max_value', headerName: 'Max', type: 'number', width: 90, editable: false },
    { field: 'format', headerName: 'Format', width: 100, editable: false },
    { field: 'show_bands', headerName: 'Bands', type: 'boolean', width: 80, editable: false },
    { field: 'hide', headerName: 'Hidden', type: 'boolean', width: 80, editable: false },
    {
      field: 'colours',
      headerName: 'Colours',
      width: 100,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: (params: any) => (params?.row?.colours ? 'Set' : '—'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 170,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: (params: any) => (
        <Stack direction="row" spacing={1}>
          <Button variant="contained" size="small" onClick={() => setLayerTarget(params.row.id)}>
            Edit
          </Button>
          <Button
            variant="text"
            color="error"
            size="small"
            onClick={() => setLayerToDelete(params.row)}
          >
            Delete
          </Button>
        </Stack>
      ),
    },
  ];

  const vectorColumns: any = [
    { field: 'order', headerName: 'Order', type: 'number', width: 80, editable: false },
    { field: 'name', headerName: 'Name', width: 200, editable: false },
    { field: 'key', headerName: 'Key', width: 160, editable: false },
    { field: 'mat_key', headerName: 'MATLAB key', width: 140, editable: false },
    { field: 'units', headerName: 'Units', width: 90, editable: false },
    {
      field: 'type_id',
      headerName: 'Type',
      width: 150,
      editable: false,
      renderCell: (params: any) => typeNameById[params?.row?.type_id] || '—',
    },
    { field: 'directional', headerName: 'Directional', type: 'boolean', width: 110, editable: false },
    { field: 'categorical', headerName: 'Categorical', type: 'boolean', width: 110, editable: false },
    { field: 'has_height', headerName: 'Height', type: 'boolean', width: 90, editable: false },
    { field: 'description', headerName: 'Description', width: 260, editable: false },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 170,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: (params: any) => (
        <Stack direction="row" spacing={1}>
          <Button variant="contained" size="small" onClick={() => setVectorTarget(params.row.id)}>
            Edit
          </Button>
          <Button
            variant="text"
            color="error"
            size="small"
            onClick={() => setVectorToDelete(params.row)}
          >
            Delete
          </Button>
        </Stack>
      ),
    },
  ];

  const layerDefaults =
    layerTarget === 'new'
      ? layerToFormValues(null, nextOrder(layers))
      : layerTarget !== null
        ? layerToFormValues(layers.find((l) => l.id === layerTarget) || null, nextOrder(layers))
        : null;

  const vectorDefaults =
    vectorTarget === 'new'
      ? vectorToFormValues(null, nextOrder(vectors))
      : vectorTarget !== null
        ? vectorToFormValues(vectors.find((v) => v.id === vectorTarget) || null, nextOrder(vectors))
        : null;

  return (
    <Dialog open onClose={handleClose} maxWidth="xl" fullWidth>
      <ModalTitle onClose={handleClose}>{`Layers and vectors — ${label}`}</ModalTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <Tabs value={tabIndex} onChange={(_: React.SyntheticEvent, v: number) => setTabIndex(v)}>
          <Tab label={`Layers (${layers.length})`} />
          <Tab label={`Vectors (${vectors.length})`} />
        </Tabs>

        <ErrorMessage message={apiError} />

        {tabIndex === 0 && (
          <>
            <div>
              <Button variant="contained" size="small" onClick={() => setLayerTarget('new')}>
                Add layer
              </Button>
            </div>
            <DataGrid
              rows={layers}
              columns={layerColumns}
              getRowId={(row) => row.id}
              loading={loading}
              initialState={{
                sorting: { sortModel: [{ field: 'order', sort: 'asc' }] },
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              pageSizeOptions={[25, 100]}
              disableRowSelectionOnClick
            />
          </>
        )}

        {tabIndex === 1 && (
          <>
            <div>
              <Button variant="contained" size="small" onClick={() => setVectorTarget('new')}>
                Add vector
              </Button>
            </div>
            <DataGrid
              rows={vectors}
              columns={vectorColumns}
              getRowId={(row) => row.id}
              loading={loading}
              initialState={{
                sorting: { sortModel: [{ field: 'order', sort: 'asc' }] },
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              pageSizeOptions={[25, 100]}
              disableRowSelectionOnClick
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ pb: 3, px: 3 }}>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>

      {layerTarget !== null && layerDefaults && (
        <CreateForm
          onCancel={() => setLayerTarget(null)}
          title={layerTarget === 'new' ? 'Add layer' : `Edit layer #${layerTarget}`}
          intro={'Order runs in increments of 10. The grid layer uses the key "model-grid" and stays hidden from the layers list.'}
          size="medium"
          allowEdit={true}
          shape={dataSourceLayerShape}
          defaultValues={layerDefaults}
          isModal
          onSuccess={() => {
            setLayerTarget(null);
            fetchChildren();
          }}
          onSubmit={async (vals: FormObject): Promise<ApiResponse> => {
            const { payload, errors } = layerFromFormValues(vals);
            if (errors.length > 0) return err(errors.join(' '), 400);
            return layerTarget === 'new'
              ? createDataSourceLayer(dataSource.id, payload)
              : updateDataSourceLayer(layerTarget as number, payload);
          }}
        />
      )}

      {layerToDelete && (
        <ConfirmDeleteModal
          title={`Delete layer #${layerToDelete.id}`}
          confirmationText={layerToDelete.key || `layer-${layerToDelete.id}`}
          confirmLabel="Delete layer"
          busy={deleting}
          intro={<>{layerToDelete.title || 'This layer'} will be removed from {label}.</>}
          warnings={[
            'This is permanent — layers have no deleted flag to restore from.',
            'Anything referencing this layer key on the map will stop rendering.',
          ]}
          onCancel={() => setLayerToDelete(null)}
          onConfirm={handleDeleteLayer}
        />
      )}

      {vectorToDelete && (
        <ConfirmDeleteModal
          title={`Delete vector #${vectorToDelete.id}`}
          confirmationText={vectorToDelete.key || `vector-${vectorToDelete.id}`}
          confirmLabel="Delete vector"
          busy={deleting}
          intro={<>{vectorToDelete.name || 'This vector'} will be removed from {label}.</>}
          warnings={[
            'This is permanent — vectors have no deleted flag to restore from.',
            'The schema response drops this key, so existing downloads that expect it will break.',
          ]}
          onCancel={() => setVectorToDelete(null)}
          onConfirm={handleDeleteVector}
        />
      )}

      {vectorTarget !== null && vectorDefaults && (
        <CreateForm
          onCancel={() => setVectorTarget(null)}
          title={vectorTarget === 'new' ? 'Add vector' : `Edit vector #${vectorTarget}`}
          intro={'The key is what the schema response and stored files are keyed on — changing it on a live source breaks existing downloads.'}
          size="medium"
          allowEdit={true}
          shape={dataSourceVectorShape(types)}
          defaultValues={vectorDefaults}
          isModal
          onSuccess={() => {
            setVectorTarget(null);
            fetchChildren();
          }}
          onSubmit={async (vals: FormObject): Promise<ApiResponse> => {
            const { payload, errors } = vectorFromFormValues(vals);
            if (errors.length > 0) return err(errors.join(' '), 400);
            return vectorTarget === 'new'
              ? createDataSourceVector(dataSource.id, payload)
              : updateDataSourceVector(vectorTarget as number, payload);
          }}
        />
      )}
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// data_source_types is a global lookup rather than a child of a data source, so
// it gets its own small modal off the data sources toolbar.
// ---------------------------------------------------------------------------

interface DataSourceTypesModalProps {
  handleClose: () => void;
}

export const DataSourceTypesModal = ({ handleClose }: DataSourceTypesModalProps) => {
  const [types, setTypes] = useState<DataSourceType[]>([]);
  const [apiError, setApiError] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState<EditTarget>(null);
  const [typeToDelete, setTypeToDelete] = useState<DataSourceType | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTypes = useCallback(async () => {
    setLoading(true);
    const { error, response, message } = await getDataSourceTypes();
    if (error === 200 && response) {
      setTypes(response?.results || []);
      setApiError([]);
    } else {
      setApiError([message || 'An error occurred while fetching vector types.']);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const handleDeleteType = useCallback(async () => {
    if (!typeToDelete) return;
    setDeleting(true);
    const { error, message } = await deleteDataSourceType(typeToDelete.id);
    setDeleting(false);
    if (error === 200) {
      setApiError([]);
      setTypeToDelete(null);
      fetchTypes();
    } else {
      setApiError([message || `Couldn't delete vector type ${typeToDelete.id}.`]);
    }
  }, [typeToDelete, fetchTypes]);

  const columns: any = [
    { field: 'id', headerName: 'ID', type: 'number', width: 70, editable: false },
    { field: 'type', headerName: 'Type', width: 160, editable: false },
    { field: 'name', headerName: 'Name', width: 200, editable: false },
    { field: 'mat_key', headerName: 'MATLAB key', width: 160, editable: false },
    { field: 'description', headerName: 'Description', width: 280, editable: false },
    {
      field: 'vectors_count',
      headerName: 'In use by',
      type: 'number',
      width: 110,
      editable: false,
      renderCell: (params: any) => {
        const count = params?.row?.vectors_count;
        if (count === undefined) return '—';
        return count === 0
          ? <Chip size="small" variant="outlined" label="Unused" />
          : `${count} vector${count === 1 ? '' : 's'}`;
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      filterable: false,
      editable: false,
      renderCell: (params: any) => {
        const row: DataSourceType = params?.row;
        const inUse = (row?.vectors_count || 0) > 0;
        return (
          <Stack direction="row" spacing={1} sx={{display: 'flex', alignItems: 'center'}}>
            <Button 
              variant="contained" 
              size="small" 
              onClick={() => setTarget(row.id)}
            >
              Edit
            </Button>
            <Tooltip title={inUse ? 'Reassign its vectors before deleting this type.' : ''}>
              <span>
                <Button
                  variant="text"
                  color="error"
                  size="small"
                  disabled={inUse}
                  onClick={() => setTypeToDelete(row)}
                >
                  Delete
                </Button>
              </span>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  const defaults =
    target === 'new'
      ? { type: '', name: '', mat_key: '', description: '' }
      : target !== null
        ? types.find((t) => t.id === target) || null
        : null;

  return (
    <Dialog open onClose={handleClose} maxWidth="lg" fullWidth>
      <ModalTitle onClose={handleClose}>Vector types</ModalTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
        <p>Types are shared across every data source — editing one changes how its vectors are grouped everywhere.</p>
        <ErrorMessage message={apiError} />
        <div>
          <Button variant="contained" size="small" onClick={() => setTarget('new')}>
            Add type
          </Button>
        </div>
        <DataGrid
          rows={types}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[25, 100]}
          disableRowSelectionOnClick
        />
      </DialogContent>
      <DialogActions sx={{ pb: 3, px: 3 }}>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>

      {typeToDelete && (
        <ConfirmDeleteModal
          title={`Delete vector type #${typeToDelete.id}`}
          confirmationText={typeToDelete.type || `type-${typeToDelete.id}`}
          confirmLabel="Delete type"
          busy={deleting}
          intro={<>{typeToDelete.name || 'This type'} will be removed from every data source.</>}
          warnings={[
            'This is permanent — types have no deleted flag to restore from.',
            'Only types with no vectors attached can be deleted.',
          ]}
          onCancel={() => setTypeToDelete(null)}
          onConfirm={handleDeleteType}
        />
      )}

      {target !== null && defaults && (
        <CreateForm
          onCancel={() => setTarget(null)}
          title={target === 'new' ? 'Add vector type' : `Edit vector type #${target}`}
          intro={'Used to group vectors in the schema response and the export UI.'}
          size="small"
          allowEdit={true}
          shape={dataSourceTypeShape}
          defaultValues={defaults}
          isModal
          onSuccess={() => {
            setTarget(null);
            fetchTypes();
          }}
          onSubmit={async (vals: FormObject): Promise<ApiResponse> =>
            target === 'new'
              ? createDataSourceType({ ...vals })
              : updateDataSourceType(target as number, { ...vals })
          }
        />
      )}
    </Dialog>
  );
};